/**
 * Ajout rapide sur une ligne (exception documentée à « une saisie ouvre une
 * page », arbitrage du plan validé le 17/10/2026) : un champ et un bouton en
 * tête d'une liste, pour ce qui se dit en quelques mots (« Rappeler M. Petit »).
 * Entrée ou le bouton enregistre ; le champ se vide et garde le focus pour la
 * suivante. Tout le reste (personne, date, précisions) passe par la page de
 * création, ouverte par « Plus d'options » avec le texte déjà saisi.
 *
 * Un appui, un envoi : le bouton se verrouille jusqu'à la fin de l'envoi
 * (`submitLock`), l'écran porte la clé d'idempotence qui protège le serveur.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { submitLock } from '../form-step.js';

export function QuickAdd(props: {
  /** Nom du champ, lu par les lecteurs d'écran (« Nouvelle tâche »). */
  label: string;
  placeholder: string;
  /** Verbe du bouton (« Ajouter »), lu quand seul le pictogramme est visible. */
  submitLabel: string;
  maxLength?: number;
  pending: boolean;
  /** Enregistre le texte ; vrai quand c'est fait (le champ se vide alors). */
  onSubmit: (value: string) => Promise<boolean>;
  /** Erreur de l'envoi, dite sous la ligne. */
  error?: string | undefined;
  /** Confirmation de l'envoi (« Ajoutée à vos tâches. »). */
  done?: string | undefined;
  /** La page de saisie complète ; `state` lui passe le texte déjà tapé. */
  more?: { label: string; to: string; state?: (value: string) => unknown } | undefined;
  disabled?: boolean;
}): ReactNode {
  const id = useId();
  const [value, setValue] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const lock = useRef<ReturnType<typeof submitLock>>(undefined /* React 19 requires this argument. skipcq: JS-W1042 */);
  lock.current ??= submitLock();
  useEffect(() => lock.current?.pendingChanged(props.pending), [props.pending]);
  const text = value.trim();
  const submit = (): void => {
    if (!text || props.disabled || !lock.current?.tryStart(props.pending)) return;
    void props.onSubmit(text).then((saved) => {
      if (!saved) return;
      setValue('');
      input.current?.focus();
    });
  };
  return (
    <div className="ui-quick-add">
      <form
        className="ui-quick-add__row"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label className="sr-only" htmlFor={`${id}-champ`}>{props.label}</label>
        <input
          ref={input}
          id={`${id}-champ`}
          className="ui-quick-add__input"
          type="text"
          value={value}
          maxLength={props.maxLength}
          placeholder={props.placeholder}
          enterKeyHint="done"
          autoComplete="off"
          disabled={props.disabled}
          aria-describedby={props.error ? `${id}-erreur` : undefined}
          aria-invalid={props.error ? true : undefined}
          onChange={(event) => setValue(event.target.value)}
        />
        <button
          type="submit"
          className="btn btn--icon ui-quick-add__submit"
          aria-label={props.pending ? `${props.submitLabel}…` : props.submitLabel}
          aria-busy={props.pending || undefined}
          disabled={!text || props.pending || props.disabled}
        >
          <Plus size={22} aria-hidden="true" />
        </button>
      </form>
      {props.error ? (
        <p className="ui-quick-add__error" id={`${id}-erreur`} role="alert">{props.error}</p>
      ) : props.done ? (
        <p className="ui-quick-add__done" role="status">{props.done}</p>
      ) : null}
      {props.more ? (
        <Link className="ui-quick-add__more" to={props.more.to} state={props.more.state?.(text)}>{props.more.label}</Link>
      ) : null}
    </div>
  );
}
